import { useRef, useState } from "react";
import { UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, galleryHeaders } from "@/lib/api";
import { uploadInChunks } from "@/lib/upload";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { GoldButton } from "@/components/Brand";

export default function ContributorUpload({ slug, event, albums, onDone }) {
  const { t, localized } = useI18n();
  const { isAdmin } = useAuth();
  const inputRef = useRef(null);
  const cancelRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [albumId, setAlbumId] = useState(albums[0]?.id || "");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length || !albumId) return;
    setBusy(true);
    setProgress(0);
    try {
      if (isAdmin) {
        const controller = new AbortController();
        cancelRef.current = controller;
        const { uploaded, errors } = await uploadInChunks({
          files,
          eventId: event.id,
          albumId,
          watermark: Boolean(event.watermark),
          downloadEnabled: true,
          published: true,
          signal: controller.signal,
          onProgress: setProgress,
        });
        toast.success(t("upload.published").replace("{n}", uploaded));
        if (errors.length) {
          toast.error(t("upload.failed").replace("{n}", errors.length));
        }
      } else {
        const form = new FormData();
        files.forEach((f) => form.append("files", f));
        form.append("album_id", albumId);
        form.append("caption_es", caption);
        const { data } = await api.post(`/events/${slug}/submissions`, form, {
          headers: galleryHeaders(slug),
        });
        toast.success(t("upload.submitted").replace("{n}", data.submitted));
      }
      setFiles([]);
      setCaption("");
      onDone?.();
    } catch (err) {
      if (err.name === "CanceledError") toast.message(t("btn.cancel"));
      else toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      cancelRef.current = null;
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="contributor-upload-form"
      className="border border-[#C69A3B] bg-[#FAFAF7] p-6"
    >
      <p className="label-mono">{isAdmin ? t("upload.adminTitle") : t("btn.uploadPhotos")}</p>
      {isAdmin && <p className="mt-2 text-xs text-[#5A5752]">{t("upload.adminHint")}</p>}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          setFiles(Array.from(e.dataTransfer.files));
        }}
        onClick={() => inputRef.current?.click()}
        data-testid="contributor-dropzone"
        className="mt-4 cursor-pointer border border-dashed border-[#C69A3B] p-8 text-center transition-colors duration-300 hover:bg-[#F6F0E4]"
      >
        <UploadCloud className="mx-auto text-[#C69A3B]" />
        <p className="mt-3 text-sm text-[#5A5752]">
          {files.length
            ? t("upload.files").replace("{n}", files.length)
            : isAdmin
              ? t("upload.publishNow")
              : t("btn.uploadPhotos")}
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          data-testid="contributor-file-input"
          className="hidden"
          onChange={(e) => setFiles(Array.from(e.target.files))}
        />
      </div>
      {files.length > 0 && files.length <= 30 && (
        <ul className="mt-3 space-y-1 text-xs text-[#5A5752]">
          {files.map((f, i) => (
            <li key={f.name + i} className="flex items-center justify-between gap-2">
              <span className="truncate">{f.name}</span>
              <button
                type="button"
                aria-label={t("btn.cancel")}
                onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <select
          value={albumId}
          onChange={(e) => setAlbumId(e.target.value)}
          data-testid="contributor-album-select"
          className="min-h-[48px] border border-[#C69A3B]/60 bg-white px-3 text-sm"
        >
          {albums.map((a) => (
            <option key={a.id} value={a.id}>
              {localized(a, "name")}
            </option>
          ))}
        </select>
        {!isAdmin && (
          <input
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder={t("contact.message")}
            data-testid="contributor-caption-input"
            className="min-h-[48px] border border-[#C69A3B]/60 bg-white px-3 text-sm"
          />
        )}
      </div>
      {busy && (
        <div className="mt-4 h-2 w-full bg-[#D8D3CB]" data-testid="contributor-progress">
          <div className="h-full bg-[#8F3329] transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
      <div className="mt-5 flex gap-3">
        <GoldButton type="submit" disabled={busy || !files.length} data-testid="contributor-submit-btn">
          {busy ? `${progress}%` : isAdmin ? t("upload.publishNow") : t("btn.submitApproval")}
        </GoldButton>
        {busy && isAdmin && (
          <GoldButton variant="outline" onClick={() => cancelRef.current?.abort()} data-testid="contributor-cancel-btn">
            {t("btn.cancel")}
          </GoldButton>
        )}
      </div>
    </form>
  );
}
